import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { creatorAPI } from '../../../api';
import { AREAS, TYPES, EVO_META, fmtDateTime, fmtDate } from '../constants';
import ProjectSections from './ProjectSections';
import CurrentDirection from './CurrentDirection';

// Item card: current state on top, EVOLUTION ↓ below — append-only history.
export default function ItemView({ itemId, t, locale, onBack, onChanged }) {
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [projects, setProjects] = useState([]);
  const [noteText, setNoteText] = useState('');
  const [busy, setBusy] = useState(false);
  const [editTitle, setEditTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');

  useEffect(() => {
    creatorAPI.get(itemId).then(({ data }) => setItem(data)).catch(() => {});
    creatorAPI.items({ type: 'project' }).then(({ data }) => setProjects(data)).catch(() => {});
  }, [itemId]);

  if (!item) return <div className="spinner" style={{ margin: '80px auto' }} />;

  const patch = async (data) => {
    const { data: updated } = await creatorAPI.update(item.id, data);
    setItem(updated);
    onChanged?.();
  };

  const addEntry = async (kind) => {
    const v = noteText.trim();
    if (!v || busy) return;
    setBusy(true);
    try {
      const { data } = await creatorAPI.addEntry(item.id, v, kind);
      setItem(data);
      setNoteText('');
      onChanged?.();
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(t.cr_delete_confirm || 'Delete this item and its whole history?')) return;
    await creatorAPI.remove(item.id);
    onChanged?.();
    onBack();
  };

  const evolution = [...(item.evolution || [])].reverse();
  const isProject = item.type === 'project';
  const parentProject = item.project_id ? projects.find((p) => p.id === item.project_id) : null;
  const projectOptions = projects.filter((p) => p.id !== item.id);

  return (
    <div className="crs-item" data-testid="creator-item-view">
      <button type="button" className="crs-backlink" onClick={onBack} data-testid="creator-item-back">
        ← {t.cr_title || 'Creator Space'}
      </button>

      {editTitle ? (
        <div className="crs-item__titleedit">
          <input
            value={titleDraft}
            onChange={(e) => setTitleDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { patch({ title: titleDraft }); setEditTitle(false); } }}
            autoFocus
            data-testid="creator-item-title-input"
          />
          <button type="button" onClick={() => { patch({ title: titleDraft }); setEditTitle(false); }} data-testid="creator-item-title-save">{t.cr_save || 'Save'}</button>
        </div>
      ) : (
        <h2
          className="crs-item__title"
          data-testid="creator-item-title"
          onClick={() => { setTitleDraft(item.title); setEditTitle(true); }}
          title={t.cr_evo_edit || 'Edit'}
        >{item.title}</h2>
      )}

      <p className="crs-item__dates" data-testid="creator-item-dates">
        {t.cr_created || 'Created'}: {fmtDateTime(item.created_at, locale)}
        <span> · </span>
        {t.cr_last_edited || 'Last edited'}: {fmtDateTime(item.updated_at, locale)}
      </p>

      {parentProject && (
        <button
          type="button"
          className="crs-partof"
          data-testid="creator-item-parent-project"
          onClick={() => navigate(`/creator/item/${parentProject.id}`)}
        >
          {t.cr_part_of || 'Part of'}: {parentProject.title}
        </button>
      )}

      {/* Organize later — quiet controls */}
      <div className="crs-item__organize" data-testid="creator-item-organize">
        <label>
          <span>{t.cr_type || 'Type'}</span>
          <select value={item.type || 'idea'} onChange={(e) => patch({ type: e.target.value })} data-testid="creator-item-type">
            {TYPES.map((ty) => <option key={ty.key} value={ty.key}>{t[ty.labelKey] || ty.key}</option>)}
          </select>
        </label>
        <label>
          <span>{t.cr_area || 'Area'}</span>
          <select value={item.area || 'brain_dump'} onChange={(e) => patch({ area: e.target.value })} data-testid="creator-item-area">
            {AREAS.map((a) => <option key={a.key} value={a.key}>{a.name}</option>)}
          </select>
        </label>
        {!isProject && projectOptions.length > 0 && (
          <label>
            <span>{t.cr_project_label || 'Project'}</span>
            <select
              value={item.project_id || ''}
              onChange={(e) => patch({ project_id: e.target.value })}
              data-testid="creator-item-project"
            >
              <option value="">{t.cr_no_project || 'None'}</option>
              {projectOptions.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
            </select>
          </label>
        )}
        {item.type === 'task' && (
          <label>
            <span>{t.cr_due || 'Due'}</span>
            <input type="date" value={item.due_date || ''} onChange={(e) => patch({ due_date: e.target.value })} data-testid="creator-item-due" />
          </label>
        )}
        <div className="crs-item__actions">
          {item.type === 'task' && (
            <button
              type="button"
              className="crs-btn"
              onClick={() => patch({ status: item.status === 'done' ? 'open' : 'done' })}
              data-testid="creator-item-toggle-done"
            >{item.status === 'done' ? (t.cr_reopen || 'Reopen') : (t.cr_mark_done || 'Mark done')}</button>
          )}
          {item.type === 'decision' && (
            <button
              type="button"
              className="crs-btn"
              onClick={() => patch({ status: item.status === 'done' ? 'open' : 'done' })}
              data-testid="creator-item-toggle-superseded"
            >{item.status === 'done' ? (t.cr_make_current || 'Make current again') : (t.cr_mark_superseded || 'Mark as superseded')}</button>
          )}
          <button type="button" className="crs-btn is-quiet" onClick={remove} data-testid="creator-item-delete">
            {t.cr_delete || 'Delete'}
          </button>
        </div>
      </div>

      {/* PROJECT VIEW: Overview · Current Direction · Decided · To do · Ideas parked · Related · Evolution */}
      {isProject && (
        <>
          <CurrentDirection item={item} t={t} onSave={(v) => patch({ direction: v })} />
          <ProjectSections project={item} t={t} locale={locale} onChanged={onChanged} />
        </>
      )}

      {/* Append a new thought — never overwrites */}
      <div className="crs-item__add" data-testid="creator-item-add-entry">
        <textarea
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
          placeholder={t.cr_note_placeholder || 'Add a thought \u2014 previous ones are never overwritten\u2026'}
          rows={2}
          data-testid="creator-item-note-input"
        />
        <div className="crs-item__addbtns">
          <button type="button" className="crs-btn" disabled={busy || !noteText.trim()} onClick={() => addEntry('note')} data-testid="creator-item-add-note">
            + {t.cr_add_note || 'Continue thinking\u2026'}
          </button>
        </div>
      </div>

      {/* EVOLUTION ↓ */}
      <div className="crs-evo" data-testid="creator-item-evolution">
        <p className="crs-section__label">{t.cr_evolution || 'Evolution'} ↓</p>
        {evolution.map((e) => {
          const meta = EVO_META[e.kind] || EVO_META.note;
          return (
            <div key={e.id} className={`crs-evo__entry is-${e.kind}`}>
              <span className="crs-evo__symbol">{meta.symbol}</span>
              <div>
                <p className="crs-evo__meta">
                  {fmtDate(e.at, locale)} · {new Date(e.at).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}
                  <em> — {t[meta.labelKey] || e.kind}</em>
                </p>
                {e.text && <p className="crs-evo__text">{e.text}</p>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
